import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { clearConsent } from "./consent";
import { CONSENT_NOTICE } from "./consentNotice";
import icon from "./assets/icon.png";
import logo from "./assets/logo.png";

/*
  Shared site chrome — navbar (+ mobile drawer) and footer.
  Extracted from LandingPage so every page renders the exact same header/footer.
  Pages differ only in how a nav link resolves (in-page scroll vs. route change),
  which is supplied through the `links` / `onNavigate` props.
*/

// The single public contact line, shown in every footer.
export const FOOTER_PHONE = "7268814449";

export const SOCIALS = {
  instagram: "https://www.instagram.com/sentiomind?igsh=NGd6NGZtdDcxMGpk&utm_source=qr",
  whatsapp: "https://wa.me/7268814449",
  linkedin: "https://www.linkedin.com/company/sentio-mind",
  facebook: "https://www.facebook.com",
};

export const InstagramIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
  </svg>
);
export const LinkedInIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M4.98 3.5C4.98 4.88 3.87 6 2.49 6 1.11 6 0 4.88 0 3.5 0 2.12 1.11 1 2.49 1c1.38 0 2.49 1.12 2.49 2.5zM.5 8h4V24h-4V8zm7.5 0h3.8v2.2h.05c.53-1 1.84-2.2 3.79-2.2 4.05 0 4.8 2.67 4.8 6.14V24h-4v-7.6c0-1.81-.03-4.14-2.52-4.14-2.53 0-2.92 1.98-2.92 4.01V24h-4V8z"/>
  </svg>
);
export const FacebookIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12a10 10 0 10-11.5 9.9v-7h-2.7v-2.9h2.7V9.8c0-2.7 1.6-4.2 4.1-4.2 1.2 0 2.4.2 2.4.2v2.6h-1.3c-1.3 0-1.7.8-1.7 1.6v2h2.9l-.5 2.9h-2.4v7A10 10 0 0022 12z"/>
  </svg>
);
export const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.137.563 4.136 1.544 5.866L0 24l6.293-1.516A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.805 9.805 0 01-5.037-1.386l-.361-.214-3.737.901.941-3.643-.234-.374A9.789 9.789 0 012.182 12C2.182 6.574 6.574 2.182 12 2.182S21.818 6.574 21.818 12 17.426 21.818 12 21.818z"/>
  </svg>
);

const SOCIAL_LIST = [
  { key: "instagram", title: "Instagram", Icon: InstagramIcon },
  { key: "whatsapp", title: "WhatsApp", Icon: WhatsAppIcon },
  { key: "linkedin", title: "LinkedIn", Icon: LinkedInIcon },
  { key: "facebook", title: "Facebook", Icon: FacebookIcon },
];

/* Same five destinations on every page; `href` is page-dependent. */
export const NAV_SECTIONS = [
  { label: "Home", id: "home" },
  { label: "Platform", id: "platform" },
  { label: "Insights", id: "insights" },
  { label: "Trust & Compliance", id: "trust" },
  { label: "Contact", id: "contact" },
];

export const chromeStyle = `
  /* ── DRAWER ANIMATIONS ── */
  @keyframes drawerFadeIn  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes drawerFadeOut { from { opacity: 1; } to { opacity: 0; } }
  @keyframes drawerSlideIn  { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes drawerSlideOut { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
  @keyframes drawerLinkIn   { from { opacity: 0; transform: translateX(18px); } to { opacity: 1; transform: translateX(0); } }

  /* ── DRAWER STYLES ── */
  .drawer-overlay {
    position: fixed;
    inset: 0;
    z-index: 9000;
    background: rgba(10, 15, 30, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    animation: drawerFadeIn 0.3s ease forwards;
  }
  .drawer-overlay.drawer-closing {
    animation: drawerFadeOut 0.38s ease forwards;
  }

  .drawer-panel {
    position: absolute;
    top: 0; right: 0; bottom: 0;
    width: min(88vw, 340px);
    background: #0a0f1e;
    border-right: 1px solid rgba(43,179,163,0.15);
    display: flex;
    flex-direction: column;
    animation: drawerSlideIn 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    overflow: hidden;
  }
  .drawer-panel.drawer-closing {
    animation: drawerSlideOut 0.38s cubic-bezier(0.55, 0, 0.75, 0.06) forwards;
  }

  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 18px;
  }

  .drawer-close-btn {
    width: 42px; height: 42px;
    border-radius: 50%;
    border: 1.5px solid rgba(43,179,163,0.25);
    background: rgba(43,179,163,0.06);
    color: rgba(255,255,255,0.7);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all 0.25s;
    flex-shrink: 0;
  }
  .drawer-close-btn:hover {
    background: rgba(43,179,163,0.18);
    border-color: rgba(43,179,163,0.5);
    color: white;
    transform: rotate(90deg);
  }

  .drawer-nav {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 28px 0 0;
    overflow-y: auto;
  }

  .drawer-link {
    display: block;
    padding: 18px 28px;
    font-family: 'Inter', sans-serif;
    font-size: 22px;
    font-weight: 500;
    color: rgba(255,255,255,0.82);
    text-decoration: none;
    letter-spacing: -0.3px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    transition: color 0.2s, background 0.2s, padding-left 0.25s;
    animation: drawerLinkIn 0.4s ease both;
    position: relative;
  }
  .drawer-link::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #2bb3a3, #6366f1);
    transform: scaleY(0);
    transition: transform 0.2s;
    border-radius: 0 2px 2px 0;
  }
  .drawer-link:hover {
    color: white;
    background: rgba(43,179,163,0.06);
    padding-left: 36px;
  }
  .drawer-link:hover::after { transform: scaleY(1); }

  .drawer-bottom {
    padding: 20px 24px 32px;
    border-top: 1px solid rgba(255,255,255,0.06);
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .drawer-socials { display: flex; gap: 10px; justify-content: flex-start; }

  .drawer-social-btn {
    width: 42px; height: 42px;
    border-radius: 50%;
    border: 1.5px solid rgba(43,179,163,0.25);
    background: rgba(43,179,163,0.06);
    color: rgba(255,255,255,0.6);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all 0.25s;
    min-width: 44px; min-height: 44px;
  }
  .drawer-social-btn:hover {
    background: linear-gradient(135deg, #2bb3a3, #6366f1);
    border-color: transparent;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(43,179,163,0.35);
  }

  .drawer-cta-btn {
    width: 100%;
    padding: 16px 24px;
    border-radius: 100px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    color: white;
    letter-spacing: 0.3px;
    transition: all 0.3s;
    min-height: 52px;
  }
  .drawer-cta-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(43,179,163,0.4);
  }

  /* ── HAMBURGER BUTTON ── */
  .hamburger-btn {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 44px; height: 44px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 10px;
    transition: background 0.2s;
    flex-shrink: 0;
    min-width: 44px;
  }
  .hamburger-btn:hover { background: rgba(43,179,163,0.08); }
  .hamburger-line {
    display: block;
    height: 2px;
    width: 22px;
    background: #475569;
    border-radius: 2px;
    transition: all 0.3s;
  }

  /* ── NAV LOGO ── */
  .nav-logo-img { width: 240px; height: 80px; object-fit: contain; }
  .nav-byline {
    font-size: 10px;
    color: #94a3b8;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding-left: 90px;
    margin-top: -10px;
    display: block;
  }

  /* ── NAV ── */
  .navbar {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 60px;
    background: rgba(240,249,255,0.85);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border-bottom: 1px solid rgba(43,179,163,0.1);
    transition: box-shadow 0.3s;
    width: 100%;
  }
  .navbar.scrolled { box-shadow: 0 4px 30px rgba(43,179,163,0.12); }
  .nav-brand { display: flex; align-items: center; gap: 12px; }
  .nav-brand-inner { display: flex; flex-direction: column; gap: 0; cursor: pointer; }
  .nav-links { display: flex; gap: 36px; }
  .nav-links a {
    font-size: 20px; font-weight: 500; color: #475569;
    text-decoration: none; letter-spacing: 0.3px;
    transition: color 0.2s;
    position: relative;
  }
  .nav-links a::after {
    content: ''; position: absolute; bottom: -3px; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    transform: scaleX(0); transition: transform 0.25s;
    transform-origin: left;
  }
  .nav-links a:hover { color: #2bb3a3; }
  .nav-links a:hover::after { transform: scaleX(1); }
  .nav-socials { display: flex; gap: 10px; }
  .nav-social-btn {
    width: 38px; height: 38px; border-radius: 50%;
    border: 1.5px solid rgba(43,179,163,0.3);
    background: transparent;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.25s;
    color: #475569;
  }
  .nav-social-btn:hover {
    background: linear-gradient(135deg, #2bb3a3, #6366f1);
    color: white; border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(43,179,163,0.35);
  }

  /* ── FLOATING PILL NAV ──
     Same component as .navbar, different shell: a compact glass pill that sits
     inside the hero's sky instead of banding across the top of it. Both states
     are driven off two custom properties so the hero → scrolled handover is one
     transition on one element, never a swap between two bars. */
  .nav-float {
    position: fixed;
    /* ~12px on phones, ~20px on tablets, 28px on desktop — plus whatever the
       notch takes, so the pill clears the status bar in landscape instead of
       sitting under it. */
    top: calc(clamp(12px, 1.6vw + 4px, 28px) + env(safe-area-inset-top, 0px));
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    /* Shell metrics live in custom properties so the brand fill can span exactly
       from the pill's inner edge to the divider without re-deriving them. */
    --nav-float-h: clamp(46px, 4.4vw, 54px);
    --nav-float-pad: clamp(14px, 1.5vw, 20px);
    --nav-float-gap: clamp(10px, 1.2vw, 14px);
    gap: var(--nav-float-gap);
    width: max-content;
    /* The pill is intrinsically sized, so this cap is the only thing standing
       between a long CTA label and a horizontally scrolling page. The insets
       are in the calc so a landscape notch eats into the pill, not the screen. */
    max-width: calc(
      100vw - 24px
      - env(safe-area-inset-left, 0px)
      - env(safe-area-inset-right, 0px)
    );
    height: var(--nav-float-h);
    padding: 0 var(--nav-float-pad);
    border-radius: 9999px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.32);
    backdrop-filter: blur(14px) saturate(150%);
    -webkit-backdrop-filter: blur(14px) saturate(150%);
    box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    --nav-float-fg: #ffffff;
    --nav-float-divider: rgba(255,255,255,0.20);
    transition:
      background 300ms ease,
      border-color 300ms ease,
      box-shadow 300ms ease,
      backdrop-filter 300ms ease,
      -webkit-backdrop-filter 300ms ease;
  }
  .nav-float.scrolled {
    background: rgba(255,255,255,0.78);
    border-color: rgba(255,255,255,0.50);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    box-shadow: 0 10px 35px rgba(15,76,92,0.12);
    --nav-float-fg: #0f4c5c;
    --nav-float-divider: rgba(15,76,92,0.10);
  }

  .nav-float-brand {
    position: relative;
    display: flex;
    align-items: center;
    gap: clamp(8px, 1vw, 12px);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    flex-shrink: 0;
  }
  .nav-float-mark {
    position: relative;
    display: block;
    height: clamp(30px, 3.4vw, 40px);
  }
  .nav-float-logo {
    position: relative;
    height: 100%;
    width: auto;
    object-fit: contain;
    display: block;
    /* No white plate under the mark any more, so the shaping does all the work:
       deepen the mid-teal strokes and carry a soft shadow so the icon reads as a
       shape against a bright sky, without shifting the brand hue. */
    filter: saturate(132%) contrast(124%) drop-shadow(0 1px 3px rgba(9,50,75,0.5));
    transition: filter 300ms ease;
  }
  .nav-float.scrolled .nav-float-logo { filter: none; }

  .nav-float-divider {
    width: 1px;
    height: 18px;
    border-radius: 1px;
    background: var(--nav-float-divider);
    transition: background 300ms ease;
    flex-shrink: 0;
  }

  .nav-float-links {
    display: flex;
    align-items: center;
    gap: clamp(12px, 1.6vw, 20px);
    /* Without this the row's min-content floor beats the pill's max-width and
       a long label pushes the pill past the viewport instead of the pill
       constraining the label. */
    min-width: 0;
  }
  .nav-float-links a {
    font-family: 'Inter', sans-serif;
    font-size: clamp(13px, 1vw, 15px);
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    text-decoration: none;
    color: var(--nav-float-fg);
    /* A bare inline link is ~16px tall. Giving it the pill's full height as a
       hit area clears 44px on touch without moving the glyph — the flexbox
       centres it either way. */
    display: inline-flex;
    align-items: center;
    height: var(--nav-float-h);
    /* Bright phone screens pass behind the pill on the way down the hero, so the
       white state carries its own shading rather than relying on the sky. */
    text-shadow: 0 1px 3px rgba(9,50,75,0.38), 0 2px 14px rgba(9,50,75,0.28);
    transition: opacity 200ms ease, transform 200ms ease, color 300ms ease, text-shadow 300ms ease;
  }
  .nav-float.scrolled .nav-float-links a { text-shadow: none; }
  .nav-float-links a:hover { opacity: 0.7; transform: translateY(-1px); }

  /* Phone line (see the ladder in MobileAppStyles.js). The pill goes to its
     compact metrics and drops the divider, which is decoration the narrow
     layout can't spare the pixels for. */
  @media (max-width: 733px) {
    .nav-float {
      --nav-float-h: 46px;
      --nav-float-pad: 12px;
      --nav-float-gap: 10px;
    }
    .nav-float-mark { height: 30px; }
    .nav-float-divider { display: none; }
    .nav-float-links { gap: 12px; }
    .nav-float-links a { font-size: 13px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .nav-float-links a:hover { transform: none; }
  }

  /* ── FOOTER ── */
  .sm-brand { display: flex; align-items: center; gap: 12px; position: relative; }
  .footer-social {
    width: 38px; height: 38px; border-radius: 50%;
    border: 1px solid rgba(43,179,163,0.25);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.25s; color: rgba(255,255,255,0.5);
    background: transparent;
  }
  .footer-social:hover {
    background: linear-gradient(135deg, #2bb3a3, #6366f1);
    border-color: transparent; color: white;
    transform: translateY(-3px); box-shadow: 0 6px 20px rgba(43,179,163,0.35);
  }
  .footer-legal-link { transition: color 0.2s; }
  .footer-legal-link:hover { color: #2bb3a3 !important; }
  /* The Cookie Settings control is a <button> in the same row, so it needs a
     focus ring of its own — the links get the UA's. */
  .footer-legal-link:focus-visible { outline: 2px solid #2bb3a3; outline-offset: 3px; border-radius: 4px; }

  /* ── CHROME RESPONSIVE ──
     Same five-rung ladder the app landing page uses (<=479 / 480-733 /
     734-1068 / 1069-1441 / >=1442), so the chrome and the page it wraps never
     restructure at different widths.

     The footer is laid out with inline styles, which is why the overrides
     below carry !important — they are the only way to reach a style attribute.
     Everything they set is fluid, so the footer scales between the rungs
     rather than stepping at them. */

  /* The footer's own gutter and type, fluid across the whole range. */
  footer#site-footer {
    padding:
      clamp(32px, 5vw, 60px)
      calc(clamp(16px, 4vw, 60px) + env(safe-area-inset-right, 0px))
      calc(20px + env(safe-area-inset-bottom, 0px))
      calc(clamp(16px, 4vw, 60px) + env(safe-area-inset-left, 0px)) !important;
  }
  /* 40px of Poppins next to a 60px mark is ~292px — four pixels wider than a
     320px screen has room for, so the brand lockup was the one thing on the
     page that could force a horizontal scroll. Both halves now scale. */
  .sm-brand img { width: clamp(38px, 9vw, 60px) !important; height: auto !important; }
  .sm-brand > div { font-size: clamp(26px, 5.4vw, 40px) !important; }
  /* Long unbroken strings — an office address line, an email — are the other
     way a fixed-width footer forces a scrollbar. */
  footer#site-footer { overflow-wrap: break-word; }

  /* The .navbar variant keeps its original 1024/768/480 lines: LandingPage is
     the only page that renders it and still runs that ladder, so moving the
     collapse point here alone would leave that page with a hamburger over a
     desktop layout between 1025 and 1068. Migrating LandingPage to the new
     ladder is the follow-up; until then the bar stays where its page is. */
  @media (max-width: 1280px) and (min-width: 1025px) {
    .navbar { padding: 18px clamp(24px, 3.2vw, 40px); }
    .nav-links { gap: clamp(20px, 2.4vw, 30px); }
    .nav-links a { font-size: clamp(16px, 1.3vw, 18px); }
  }

  @media (max-width: 1024px) {
    .hamburger-btn { display: flex; }
    .nav-links { display: none !important; }
    .nav-socials { display: none !important; }
    .navbar { padding: 14px clamp(14px, 3vw, 24px); }
    .nav-logo-img { width: clamp(130px, 18vw, 180px); height: auto; }
    .nav-byline { padding-left: clamp(48px, 6.6vw, 66px); font-size: clamp(9px, 0.9vw, 10px); }
  }

  /* The footer is shared by both pages but is a self-contained band — it can
     restructure on the new ladder without lining up with anything above it. */
  @media (max-width: 1068px) {
    footer > div:first-child {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 32px !important;
    }
    footer > div:first-child > div:first-child { grid-column: 1 / -1; }
  }

  @media (max-width: 733px) {
    footer > div:first-child {
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 36px !important;
    }
    footer > div:first-child > div:first-child { grid-column: auto; }
    footer > div:last-child {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 6px !important;
    }
    footer#site-footer > div:last-child p { font-size: 13px !important; }
  }

  @media (max-width: 479px) {
    .drawer-panel { width: 92vw; }
    .drawer-link { font-size: 20px; padding: 16px 22px; }
    .drawer-header { padding: 16px 18px 14px; }
    .drawer-bottom { padding: 16px 18px calc(24px + env(safe-area-inset-bottom, 0px)); gap: 16px; }

    footer > div:first-child {
      gap: 28px !important;
      margin-bottom: 32px !important;
      padding-bottom: 32px !important;
    }
  }

  /* Landscape phone — the drawer is the one piece of chrome that can run out
     of vertical room, so its rhythm tightens rather than its type shrinking. */
  @media (max-height: 560px) and (orientation: landscape) {
    .drawer-nav { padding-top: 16px; }
    .drawer-link { padding: 13px 28px; font-size: 18px; }
    .drawer-bottom { padding: 14px 24px calc(16px + env(safe-area-inset-bottom, 0px)); }
  }

  /* ── TOUCH TARGETS ──
     Last in the sheet on purpose. The 38px circles are declared further up at
     the same specificity, so a coarse-pointer override written above them
     loses the cascade and silently does nothing — measured at 38x38 on an
     emulated phone before this block moved down here.

     Nothing here changes a size on a mouse. The circles grow to 44px; the
     brand pill and the two footer text links get a 44px tap box while their
     type stays exactly where it was, so the footer reads the same and only
     the target grows around it. */
  @media (pointer: coarse) {
    .nav-social-btn, .footer-social, .drawer-close-btn { width: 44px; height: 44px; }
    .nav-float-brand { min-height: 44px; }
    footer#site-footer a { display: inline-flex; align-items: center; min-height: 44px; }
    .navbar a, .navbar button, footer a, footer button { touch-action: manipulation; }
  }
`;

const openExternal = (url) => window.open(url, "_blank", "noopener");

/**
 * Fixed navbar + mobile drawer.
 * @param links      [{ label, id, href }] — href is only the anchor fallback; navigation runs through onNavigate.
 * @param onNavigate (id) => void — page decides whether that means "scroll" or "route".
 * @param onOpenDemo () => void — drawer CTA.
 * @param ctaLabel string — text on that drawer CTA.
 * @param variant    "bar" (full-width header) | "floating" (glass pill over the hero).
 * @param heroRef    floating only — the section the pill is transparent over. Its
 *                   bottom edge, not a fixed pixel count, is what flips the state.
 */
export function SiteNav({ links = NAV_SECTIONS, onNavigate, onOpenDemo, ctaLabel = "Have an Enquiry?", variant = "bar", heroRef }) {
  const floating = variant === "floating";
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    // The pill stays transparent for as long as the hero is behind it; the bar
    // only needs to know it has left the very top of the page.
    const onScroll = () => {
      if (!floating) {
        setScrolled(window.scrollY > 40);
        return;
      }
      const hero = heroRef?.current;
      const navBottom = (navRef.current?.getBoundingClientRect().bottom ?? 0) + 24;
      setScrolled(hero ? hero.getBoundingClientRect().bottom <= navBottom : window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [floating, heroRef]);

  // Publish the space the navbar occupies so pages can offset their scroll
  // anchors. The pill floats, so what matters is its bottom edge, not its height.
  useEffect(() => {
    const setNavHeight = () => {
      if (navRef.current) {
        const { height, top } = navRef.current.getBoundingClientRect();
        const occupied = floating ? Math.round(top + height) : Math.round(height);
        document.documentElement.style.setProperty("--nav-height", `${occupied}px`);
      }
    };
    setNavHeight();
    const ro = new ResizeObserver(setNavHeight);
    if (navRef.current) ro.observe(navRef.current);
    window.addEventListener("resize", setNavHeight);
    return () => {
      window.removeEventListener("resize", setNavHeight);
      ro.disconnect();
    };
  }, [floating]);

  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  const closeDrawer = useCallback(() => {
    setDrawerClosing(true);
    setTimeout(() => {
      setDrawerOpen(false);
      setDrawerClosing(false);
    }, 380);
  }, []);

  const handleDrawerNav = useCallback((id) => {
    closeDrawer();
    setTimeout(() => onNavigate?.(id), 420);
  }, [closeDrawer, onNavigate]);

  if (floating) {
    return (
      <>
        <style>{chromeStyle}</style>

        <nav ref={navRef} className={`nav-float${scrolled ? " scrolled" : ""}`}>
          <button
            className="nav-float-brand"
            onClick={() => onNavigate?.("home")}
            aria-label="Sentio Mind — back to top"
          >
            <span className="nav-float-mark">
              <img src={icon} alt="Sentio Mind" className="nav-float-logo" />
            </span>
            <span className="nav-float-divider" aria-hidden="true" />
          </button>

          <div className="nav-float-links">
            {links.map((item) => (
              <a
                key={item.id}
                href={item.href ?? `#${item.id}`}
                onClick={(e) => { e.preventDefault(); onNavigate?.(item.id); }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      <style>{chromeStyle}</style>

      <nav ref={navRef} className={`navbar${scrolled ? " scrolled" : ""}`}>
        <div className="nav-brand">
          <div
            className="nav-brand-inner"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate?.("home")}
            onKeyDown={(e) => e.key === "Enter" && onNavigate?.("home")}
          >
            <img src={icon} alt="Sentio Mind" className="nav-logo-img" />
            <span className="nav-byline">by SGRG Group</span>
          </div>
        </div>

        <div className="nav-links">
          {links.map((item) => (
            <a
              key={item.id}
              href={item.href ?? `#${item.id}`}
              onClick={(e) => { e.preventDefault(); onNavigate?.(item.id); }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="nav-socials">
          {SOCIAL_LIST.map(({ key, title, Icon }) => (
            <button key={key} className="nav-social-btn" title={title} onClick={() => openExternal(SOCIALS[key])}>
              <Icon />
            </button>
          ))}
        </div>

        {/* Hamburger — mobile/tablet only */}
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </nav>

      {drawerOpen && (
        <div className={`drawer-overlay${drawerClosing ? " drawer-closing" : ""}`} onClick={closeDrawer}>
          <div className={`drawer-panel${drawerClosing ? " drawer-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img src={logo} alt="Sentio Mind" style={{ width: 38, height: 57, objectFit: "contain" }} />
                  <div style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 24,
                    fontWeight: 600,
                    letterSpacing: "-0.2px",
                    color: "#ffffff",
                  }}>
                    Sentio <span style={{ color: "#41b7b6" }}>Mind</span>
                  </div>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={closeDrawer} aria-label="Close menu">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="3" x2="19" y2="19"/><line x1="19" y1="3" x2="3" y2="19"/>
                </svg>
              </button>
            </div>

            <nav className="drawer-nav">
              {links.map((item, i) => (
                <a
                  key={item.id}
                  href={item.href ?? `#${item.id}`}
                  className="drawer-link"
                  style={{ animationDelay: `${0.05 + i * 0.06}s` }}
                  onClick={(e) => { e.preventDefault(); handleDrawerNav(item.id); }}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="drawer-bottom">
              <div className="drawer-socials">
                {SOCIAL_LIST.map(({ key, title, Icon }) => (
                  <button key={key} className="drawer-social-btn" title={title} onClick={() => openExternal(SOCIALS[key])}>
                    <Icon size={18} />
                  </button>
                ))}
              </div>
              <button
                className="drawer-cta-btn"
                onClick={() => { closeDrawer(); setTimeout(() => onOpenDemo?.(), 420); }}
              >
                {ctaLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * @param contactEmail string — overrides the address shown in the Enquiries
 *   column. Every page shows one shared inbox instead of direct lines to the
 *   leadership team; only the legal pages point somewhere else.
 */
export function SiteFooter({ contactEmail = "enquiry@sentiomind.in" }) {
  return (
    <footer id="site-footer" style={{
      background: "#0a0f1e",
      borderTop: "1px solid rgba(43,179,163,0.1)",
      padding: "60px 60px 32px",
      width: "100%",
    }}>
      {/* Top grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr",
        gap: 48,
        marginBottom: 48,
        paddingBottom: 48,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>

        {/* Column 1 — Brand */}
        <div>
          <div className="sm-brand">
            <img src={logo} alt="Sentio Mind" style={{ width: 60, height: 90, objectFit: "contain", borderRadius: 0 }} />
            <div style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 40,
              fontWeight: 600,
              letterSpacing: "-0.2px",
              color: "#ffffff",
            }}>
              Sentio <span style={{ color: "#41b7b6" }}>Mind</span>
            </div>
          </div>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
            A Brand of
          </p>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: 24,
            background: "linear-gradient(90deg, #2bb3a3, #6366f1)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginBottom: 14,
          }}>
            SGRG Technologies Private Limited®
          </p>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", lineHeight: 1.7, maxWidth: 280 }}>
            From patterns to prevention — transforming environments through behaviour intelligence.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            {SOCIAL_LIST.map(({ key, title, Icon }) => (
              <button key={key} className="footer-social" title={title} onClick={() => openExternal(SOCIALS[key])}>
                <Icon size={16} />
              </button>
            ))}
          </div>
        </div>

        {/* Column 2 — Enquiries (one shared inbox, no personal contacts) */}
        <div>
          <p style={{ fontSize: 15, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#2bb3a3", marginBottom: 20 }}>
            Enquiries
          </p>
          <a
            href={`mailto:${contactEmail}`}
            style={{ fontSize: 18, color: "#2bb3a3", textDecoration: "none", fontWeight: 500, wordBreak: "break-word" }}
          >
            {contactEmail}
          </a>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", marginTop: 16, marginBottom: 20 }}>
            Contact
          </p>
          <a href={`tel:+91${FOOTER_PHONE}`} style={{ fontSize: 18, color: "#2bb3a3", textDecoration: "none", fontWeight: 500 }}>
            {FOOTER_PHONE}
          </a>
        </div>

        {/* Column 3 — Offices */}
        <div>
          <p style={{ fontSize: 15, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#2bb3a3", marginBottom: 20 }}>
            Offices
          </p>
          {[
            {
              label: "Head Office", city: "Kanpur",
              address: "1111, Y Block, Kidwai Nagar,\nKanpur, Uttar Pradesh 208011",
              maps: "https://www.google.com/maps/search/1111+Y+Block+Kidwai+Nagar+Kanpur+208011",
            },
            {
              label: "Corporate Office", city: "Lucknow",
              address: "903–904, 9th Floor, Shalimar Iridium,\nVijaipur Colony, Vibhuti Khand,\nGomti Nagar, Lucknow, UP 226010",
              maps: "https://www.google.com/maps/search/Shalimar+Iridium+Gomti+Nagar+Lucknow+226010",
            },
          ].map(({ label, city, address, maps }) => (
            <div key={label} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 18, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{label}</span>
                <span style={{ fontSize: 16, color: "rgba(255,255,255,0.25)" }}>·</span>
                <span style={{ fontSize: 16, color: "#2bb3a3", fontWeight: 500 }}>{city}</span>
              </div>
              <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", lineHeight: 1.75, whiteSpace: "pre-line", marginBottom: 6 }}>
                {address}
              </p>
              <a href={maps} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#6366f1", textDecoration: "none", fontWeight: 500 }}>
                View on Maps →
              </a>
            </div>
          ))}
        </div>

      </div>

      {/* Bottom bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <p style={{ fontSize: 18, color: "rgba(255,255,255,0.22)" }}>
          {new Date().getFullYear()} SGRG Technologies Private Limited. Sentio Mind™. All rights reserved.
        </p>
        {/* The two legal documents, plus the way back to the cookie choice.
            Routes, not anchors, so leaving the page never costs a full
            reload. */}
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          {[
            { to: "/privacy", label: "Privacy Policy" },
            { to: "/terms", label: "Terms of Service" },
          ].map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="footer-legal-link"
              style={{ fontSize: 18, color: "rgba(255,255,255,0.45)", textDecoration: "none", fontWeight: 500 }}
            >
              {label}
            </Link>
          ))}
          {/*
            Withdraws the marketing choice by forgetting the stored answer,
            which puts the visitor back to "not asked" — and therefore back to
            no marketing tracking until they answer again. There is no banner
            to reopen and, with the waitlist dialog retired, nowhere that asks
            again, so this is purely the withdrawal control.

            It does not touch the Pixel itself; clearing the answer notifies
            the consent store, and MetaPixel.jsx stands any loaded pixel down
            from there.

            Limitation, by design: fbevents.js cannot be unloaded once it is on
            the page. Withdrawing stops every further event immediately, and
            re-accepting takes effect from the next page load rather than
            mid-page. See CONSENT_INTEGRATION.md.
          */}
          <button
            type="button"
            className="footer-legal-link"
            onClick={clearConsent}
            style={{
              fontFamily: "inherit", fontSize: 18, fontWeight: 500,
              color: "rgba(255,255,255,0.45)",
              background: "none", border: "none", padding: 0, cursor: "pointer",
            }}
          >
            {CONSENT_NOTICE.reopenLabel}
          </button>
        </div>

        <p style={{ fontSize: 18, color: "rgba(255,255,255,0.18)" }}>
          Behaviour Intelligence · DPDP Act 2023 Compliant
        </p>
      </div>
    </footer>
  );
}
